import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, MapPin, Loader2, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { Section, Breadcrumb } from "@/components/Sections";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BUSINESS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "AC Installation",
  "AC Deep Cleaning",
  "AC Repair / Inspection",
  "AC Gas Refill",
  "Other Enquiry",
];

const EMPTY = { name: "", phone: "", email: "", service: "", message: "", company: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service || !form.message.trim()) {
      toast.error("Please complete your name, phone, service and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        service: form.service,
        message: form.message.trim(),
        company: form.company,
      });
      toast.success("Enquiry sent. NorthWork Cyprus will be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly on " + BUSINESS.phoneDisplay + ".");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact NorthWork Cyprus | Air Conditioning Paphos"
        description="Contact NorthWork Cyprus for AC installation, deep cleaning, repair, diagnostics or gas refill in Paphos. Call +357 97 837 349 or send an enquiry."
        path="/contact"
      />
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />

      <Section className="pt-6 pb-16 lg:pt-8 lg:pb-24">
        <div className="container-x grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left: details */}
          <div className="lg:col-span-5">
            <Reveal delay={0.05} variant="up">
              <h1 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
                Contact NorthWork Cyprus
              </h1>
            </Reveal>
            <Reveal delay={0.1} variant="up" className="mt-5 max-w-md">
              <p className="text-base leading-relaxed text-charcoal-muted lg:text-lg">
                Need AC installation, deep cleaning, repair or gas refill in Paphos? Contact NorthWork
                Cyprus to discuss the job, arrange an inspection or book a visit.
              </p>
            </Reveal>

            <Reveal delay={0.15} variant="up" className="mt-10">
              <span className="font-mono-t text-[11px] uppercase tracking-[0.18em] text-charcoal-muted">
                Call us directly
              </span>
              <a
                href={`tel:${BUSINESS.phoneTel}`}
                data-testid="contact-phone"
                className="mt-3 block font-heading text-4xl font-extrabold tracking-tight text-charcoal transition-colors hover:text-brand sm:text-5xl"
              >
                {BUSINESS.phoneDisplay}
              </a>
              <p className="mt-3 flex items-center gap-2 text-[13px] text-charcoal-muted">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-brand" />
                Servicing Paphos and surrounding areas.
              </p>
            </Reveal>

            <Reveal delay={0.22} variant="up" className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={`tel:${BUSINESS.phoneTel}`}
                data-testid="contact-call-button"
                className="group inline-flex items-center justify-center gap-2.5 rounded-sm bg-brand px-7 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-brand-hover"
              >
                <Phone className="h-4 w-4" /> Call Now
              </a>
              <a
                href={BUSINESS.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="group inline-flex items-center justify-center gap-2.5 rounded-sm border border-charcoal/20 px-7 py-4 text-sm font-medium tracking-wide text-charcoal transition-colors hover:border-charcoal/50 hover:bg-surface"
              >
                <MessageCircle className="h-4 w-4 text-brand" /> WhatsApp Us
              </a>
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <Reveal variant="up">
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="border border-border bg-white p-7 sm:p-10"
              >
                {/* Honeypot — hidden from users, catches bots */}
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={update("company")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
                />
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Name" required>
                    <Input
                      data-testid="form-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className="h-12 rounded-sm border-border focus-visible:ring-brand"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <Input
                      data-testid="form-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="Your phone number"
                      className="h-12 rounded-sm border-border focus-visible:ring-brand"
                    />
                  </Field>
                  <Field label="Email" optional>
                    <Input
                      data-testid="form-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="Your email"
                      className="h-12 rounded-sm border-border focus-visible:ring-brand"
                    />
                  </Field>
                  <Field label="Service Required" required>
                    <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                      <SelectTrigger
                        data-testid="form-service"
                        className="h-12 rounded-sm border-border focus:ring-brand"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt} data-testid={`service-option-${opt}`}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-6">
                  <Field label="Message" required>
                    <Textarea
                      data-testid="form-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us briefly what you need, the property location and how many AC units are involved."
                      rows={5}
                      className="rounded-sm border-border focus-visible:ring-brand"
                    />
                  </Field>
                </div>

                <button
                  type="submit"
                  data-testid="form-submit"
                  disabled={submitting}
                  className="group mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-sm bg-brand px-7 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-brand-hover disabled:opacity-60 sm:w-auto"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send Enquiry
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                <p className="mt-4 text-[12px] text-charcoal-muted">
                  We only ask for the information needed to respond to your enquiry.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </Section>
    </>
  );
}

function Field({ label, required, optional, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-[13px] font-medium text-charcoal">
        {label}
        {required && <span className="ml-1 text-brand">*</span>}
        {optional && <span className="ml-1 font-normal text-charcoal-muted">(optional)</span>}
      </Label>
      {children}
    </div>
  );
}
